import { z } from "zod";

/**
 * Lançamento de conta a pagar com N parcelas (paridade com o desktop, espelho do
 * Contas a Receber).
 *
 * `valorParcela` é o valor de UMA parcela — o título vale
 * `valorParcela × qtdParcelas`. O desktop trabalha assim (`msk_valororiginal ×
 * msk_qtd_parcelas`), o que evita a divisão do total e a sobra de centavos.
 */
export const contaPagarSchema = z.object({
  fornecedorId: z.coerce
    .number({ invalid_type_error: "ID do fornecedor deve ser um número" })
    .int()
    .min(1, "Fornecedor é obrigatório"),
  grupoDespesasId: z.coerce
    .number({ invalid_type_error: "Grupo de despesas inválido" })
    .int()
    .min(1, "Grupo de despesas é obrigatório"),
  tipoDespesasId: z.coerce
    .number({ invalid_type_error: "Tipo de despesas inválido" })
    .int()
    .min(1, "Tipo de despesas é obrigatório"),
  centroCustoId: z.coerce
    .number({ invalid_type_error: "Centro de custo inválido" })
    .int()
    .min(1, "Centro de custo é obrigatório"),
  contaContabilId: z.coerce
    .number({ invalid_type_error: "Conta contábil inválida" })
    .int()
    .min(1, "Conta contábil é obrigatória"),
  dtEmissao: z.string().min(1, "Data de emissão é obrigatória"),
  /** Vencimento da 1ª parcela; as demais saem de `diasParcelas`. */
  dtVencimento: z.string().min(1, "Data de vencimento é obrigatória"),
  valorParcela: z.coerce
    .number({ invalid_type_error: "Valor deve ser um número" })
    .positive("Valor deve ser maior que zero")
    .max(999999999.99, "Valor muito alto"),
  numDocumento: z.string()
    .trim()
    .max(50, "Número do documento deve ter no máximo 50 caracteres")
    .optional(),
  qtdParcelas: z.coerce.number().int().min(1, "Mínimo 1 parcela").max(360, "Máximo 360 parcelas").default(1),
  /** Deslocamentos em dias a partir do 1º vencimento: "7,14,28". Vazio → 30 em 30. */
  diasParcelas: z.string()
    .trim()
    .regex(/^(\s*\d+\s*)(,\s*\d+\s*)*$/, "Use números separados por vírgula (ex.: 7,14,28)")
    .optional()
    .or(z.literal("")),
  /** Mantém o dia do mês do 1º vencimento nas demais parcelas. */
  diaFixo: z.boolean().default(false),
  lancamento: z.string().trim().max(200, "Descrição muito longa").optional(),
});

export type ContaPagarFormData = z.infer<typeof contaPagarSchema>;

/**
 * Variante para EDIÇÃO: grupo/tipo/centro/conta contábil ficam OPCIONAIS.
 *
 * Motivo: a maioria dos títulos deste cliente veio migrada do Horus SEM
 * classificação de despesa (`grupo/tipo/centro = 0`). Exigi-los no lançamento
 * faz sentido; exigi-los para EDITAR trava a correção de valor/vencimento de um
 * título legado — o usuário não deveria ser obrigado a classificar só para
 * editar. Os selects continuam disponíveis para quem quiser classificar.
 * O valor 0 (não classificado) passa a ser aceito.
 */
export const contaPagarEditSchema = contaPagarSchema.extend({
  grupoDespesasId: z.coerce.number().int().min(0).optional(),
  tipoDespesasId:  z.coerce.number().int().min(0).optional(),
  centroCustoId:   z.coerce.number().int().min(0).optional(),
  contaContabilId: z.coerce.number().int().min(0).optional(),
});

/**
 * Espelha `CronogramaParcelas.Gerar` do backend para PRÉ-VISUALIZAR os
 * vencimentos enquanto o usuário digita. A verdade continua sendo o servidor —
 * isto é só para o usuário conferir o carnê antes de gravar.
 */
export function preverVencimentos(
  primeiroVencimento: string,
  qtdParcelas: number,
  diasParcelas?: string,
  diaFixo = false,
): Date[] {
  if (!primeiroVencimento) return [];

  // `new Date("2026-01-10")` é interpretado como UTC e volta um dia no fuso do
  // Brasil; montar por componentes mantém a data local.
  const [ano, mes, dia] = primeiroVencimento.split("-").map(Number);
  if (!ano || !mes || !dia) return [];

  const primeiro = new Date(ano, mes - 1, dia);
  const qtd = Math.min(Math.max(1, qtdParcelas || 1), 360);

  const lista = (diasParcelas ?? "")
    .split(",")
    .map((p) => parseInt(p.trim(), 10))
    .filter((n) => Number.isFinite(n));

  const ultimo = lista.length > 0 ? lista[lista.length - 1] : 0;
  const intervalo = ultimo > 0 ? ultimo : 30;

  const somarDias = (d: Date, n: number) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);

  const datas = [primeiro];
  let cursor = primeiro;

  for (let i = 1; i < qtd; i++) {
    cursor = lista.length >= i
      ? somarDias(primeiro, lista[i - 1])
      : somarDias(cursor, intervalo);

    if (diaFixo) {
      const ultimoDiaDoMes = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
      datas.push(new Date(cursor.getFullYear(), cursor.getMonth(),
        Math.min(primeiro.getDate(), ultimoDiaDoMes)));
    } else {
      datas.push(cursor);
    }
  }

  return datas;
}
